import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";

interface HeroCarouselProps {
  items: Drama[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="flex h-[520px] w-full items-center justify-center bg-background-200 text-sm text-foreground-600">
        暂无相关内容
      </div>
    );
  }

  const current = items[active];

  return (
    <section className="relative h-[560px] md:h-[620px] w-full overflow-hidden bg-foreground-950">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} ${item.genres.join(" ")}`}
            className={`h-full w-full object-cover object-top ${index === active ? "animate-slow-zoom" : ""}`}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-foreground-950/40" />

      <div className="relative z-10 flex h-full w-full items-end px-4 pb-14 md:px-8 md:pb-20">
        <div className="w-full max-w-6xl mx-auto">
          <div className="max-w-2xl">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2 py-1 text-xs font-medium text-background-50">
                焦点推荐
              </span>
              <span className="rounded-md border border-background-50/40 px-2 py-1 text-xs text-background-100">
                {current.year} · {current.category === "movie" ? "电影" : "电视剧"}
              </span>
              <span className="flex items-center gap-1 text-xs text-accent-300">
                <i className="ri-star-fill" />
                {current.rating.toFixed(1)} 分
              </span>
            </div>
            <h2 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              {current.title}
            </h2>
            <p className="mt-4 hidden text-sm leading-relaxed text-background-200 sm:block md:text-base">
              {current.summary}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-background-300">
              <span>导演 {current.director}</span>
              <span className="text-background-500">|</span>
              <span>主演 {current.cast.slice(0, 3).join(" / ")}</span>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-base" />
                查看详情
              </Link>
              <Link
                to={`/category?genre=${encodeURIComponent(current.genres[0])}`}
                className="inline-flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap cursor-pointer"
              >
                更多{current.genres[0]}作品
              </Link>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              {items.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`切换到 ${item.title}`}
                  onClick={() => setActive(index)}
                  className={`h-1.5 rounded-full transition-all cursor-pointer ${
                    index === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/40"
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-background-300">
              {String(active + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
            </span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-14 right-4 z-10 hidden items-center gap-2 md:flex md:right-8">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => setActive((prev) => (prev - 1 + items.length) % items.length)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => setActive((prev) => (prev + 1) % items.length)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </section>
  );
}